import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Grid3X3, MessageCircle, User, Heart, Zap, Eye, ArrowLeft, Lock, Unlock, Check, X } from 'lucide-react';
import BottomNav from '../components/BottomNav';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sampleImages = [
  'https://images.unsplash.com/photo-1635631191842-cc5043d56fc4?crop=entropy&cs=srgb&fm=jpg&q=85&w=200',
  'https://images.unsplash.com/photo-1762708546140-7aea0b34ecac?crop=entropy&cs=srgb&fm=jpg&q=85&w=200',
  'https://images.unsplash.com/photo-1582439170934-d089aa10abda?crop=entropy&cs=srgb&fm=jpg&q=85&w=200',
  'https://images.unsplash.com/photo-1635631191694-8fc637d53429?crop=entropy&cs=srgb&fm=jpg&q=85&w=200'
];

const Activity = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('taps');
  const [receivedTaps, setReceivedTaps] = useState([]);
  const [sentTaps, setSentTaps] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [viewers, setViewers] = useState([]);
  const [privateRequests, setPrivateRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [grantingAccess, setGrantingAccess] = useState({});

  useEffect(() => {
    fetchData();
    // Mark activity as seen when visiting this page
    axios.post(`${API}/activity/mark-seen`).catch(() => {});
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'taps') {
        const [received, sent] = await Promise.all([
          axios.get(`${API}/users/taps/received`),
          axios.get(`${API}/users/taps/sent`)
        ]);
        setReceivedTaps(received.data);
        setSentTaps(sent.data);
      } else if (activeTab === 'favorites') {
        const response = await axios.get(`${API}/users/favorites/list`);
        setFavorites(response.data);
      } else if (activeTab === 'viewers') {
        const response = await axios.get(`${API}/users/viewers`);
        setViewers(response.data);
      } else if (activeTab === 'requests') {
        const response = await axios.get(`${API}/notifications/private-requests`);
        setPrivateRequests(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGrantAccess = async (userId, notificationId) => {
    setGrantingAccess(prev => ({ ...prev, [notificationId]: true }));
    try {
      await axios.post(`${API}/users/private-access/grant/${userId}`);
      // Mark notification as actioned
      await axios.post(`${API}/notifications/${notificationId}/action`, { action: 'granted' });
      toast.success('Access granted! They can now see your private photos');
      // Remove from list
      setPrivateRequests(prev => prev.filter(r => r.id !== notificationId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant access');
    } finally {
      setGrantingAccess(prev => ({ ...prev, [notificationId]: false }));
    }
  };

  const handleDenyRequest = async (notificationId) => {
    setGrantingAccess(prev => ({ ...prev, [notificationId]: true }));
    try {
      await axios.post(`${API}/notifications/${notificationId}/action`, { action: 'denied' });
      toast.info('Request dismissed');
      setPrivateRequests(prev => prev.filter(r => r.id !== notificationId));
    } catch (error) {
      toast.error('Failed to dismiss request');
    } finally {
      setGrantingAccess(prev => ({ ...prev, [notificationId]: false }));
    }
  };

  const getPhotoUrl = (user, index = 0) => {
    if (user?.public_photos && user.public_photos.length > 0) {
      const photo = user.public_photos[0];
      if (photo.startsWith('data:') || photo.startsWith('http')) {
        return photo;
      }
      return `data:image/jpeg;base64,${photo}`;
    }
    return sampleImages[index % sampleImages.length];
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const UserCard = ({ user, index, showTime, timeLabel }) => (
    <button
      data-testid={`user-card-${user.id}`}
      onClick={() => navigate(`/profile/${user.id}`)}
      className="flex items-center gap-3 p-3 hover:bg-white/5 rounded-lg transition-colors w-full text-left"
    >
      <div className="relative flex-shrink-0">
        <img
          src={getPhotoUrl(user, index)}
          alt={user.display_name || user.username}
          className="w-14 h-14 rounded-lg object-cover"
        />
        {user.is_online && (
          <div className="absolute bottom-0 right-0 w-3 h-3 bg-[#08D9D6] rounded-full border-2 border-[#050505]" />
        )}
        {user.is_mutual && (
          <div className="absolute -top-1 -right-1 w-5 h-5 bg-[#FF2E63] rounded-full flex items-center justify-center">
            <Zap className="w-3 h-3 text-white" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-[#EDEDED] truncate">
          {user.display_name || user.username}
          {user.age && <span className="font-normal text-[#A1A1AA]">, {user.age}</span>}
        </h3>
        {user.is_mutual && (
          <p className="text-xs text-[#FF2E63]">Mutual tap!</p>
        )}
        {showTime && timeLabel && (
          <p className="text-xs text-[#52525B]">{timeLabel}</p>
        )}
      </div>
      {user.distance !== undefined && user.distance !== null && (
        <span className="text-xs text-[#52525B] font-mono">{user.distance} km</span>
      )}
    </button>
  );

  const EmptyState = ({ icon: Icon, title, description }) => (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <Icon className="w-12 h-12 text-[#52525B] mb-4" />
      <h3 className="text-lg font-semibold text-[#EDEDED] mb-2">{title}</h3>
      <p className="text-sm text-[#A1A1AA]">{description}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050505] pb-20">
      {/* Header */}
      <header className="sticky top-0 z-40 glass">
        <div className="flex items-center gap-3 px-4 py-3">
          <Button
            data-testid="back-btn"
            variant="ghost"
            size="icon"
            onClick={() => navigate('/grid')}
            className="text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-xl font-bold text-[#FF2E63]">Activity</h1>
        </div>
      </header>

      {/* Tabs */}
      <div className="px-4 pt-4">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4 bg-white/5 mb-4">
            <TabsTrigger 
              data-testid="taps-tab"
              value="taps" 
              className="data-[state=active]:bg-[#FF2E63] data-[state=active]:text-white flex items-center gap-1 text-xs px-2"
            >
              <Zap className="w-3 h-3" />
              Taps
            </TabsTrigger>
            <TabsTrigger 
              data-testid="requests-tab"
              value="requests"
              className="data-[state=active]:bg-[#FF2E63] data-[state=active]:text-white flex items-center gap-1 text-xs px-2"
            >
              <Lock className="w-3 h-3" />
              Requests
            </TabsTrigger>
            <TabsTrigger 
              data-testid="favorites-tab"
              value="favorites"
              className="data-[state=active]:bg-[#FF2E63] data-[state=active]:text-white flex items-center gap-1 text-xs px-2"
            >
              <Heart className="w-3 h-3" />
              Favorites
            </TabsTrigger>
            <TabsTrigger 
              data-testid="viewers-tab"
              value="viewers"
              className="data-[state=active]:bg-[#FF2E63] data-[state=active]:text-white flex items-center gap-1 text-xs px-2"
            >
              <Eye className="w-3 h-3" />
              Viewers
            </TabsTrigger>
          </TabsList>

          {/* Taps Tab */}
          <TabsContent value="taps" className="space-y-4">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-2 border-[#FF2E63] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <>
                {/* Received Taps */}
                <div className="glass rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-[#A1A1AA] mb-3 flex items-center gap-2">
                    <Zap className="w-4 h-4 text-[#FF2E63]" />
                    Tapped You ({receivedTaps.length})
                  </h3>
                  {receivedTaps.length === 0 ? (
                    <p className="text-sm text-[#52525B] py-4 text-center">No taps received yet</p>
                  ) : (
                    <div className="space-y-1">
                      {receivedTaps.map((user, i) => (
                        <UserCard key={user.id} user={user} index={i} />
                      ))}
                    </div>
                  )}
                </div>

                {/* Sent Taps */}
                <div className="glass rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-[#A1A1AA] mb-3 flex items-center gap-2">
                    <Zap className="w-4 h-4" />
                    You Tapped ({sentTaps.length})
                  </h3>
                  {sentTaps.length === 0 ? (
                    <p className="text-sm text-[#52525B] py-4 text-center">You haven't tapped anyone yet</p>
                  ) : (
                    <div className="space-y-1">
                      {sentTaps.map((user, i) => (
                        <UserCard key={user.id} user={user} index={i} />
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </TabsContent>

          {/* Requests Tab */}
          <TabsContent value="requests">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-2 border-[#FF2E63] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : privateRequests.length === 0 ? (
              <EmptyState 
                icon={Lock}
                title="No private photo requests"
                description="When someone wants to see your private photos, their request will appear here"
              />
            ) : (
              <div className="glass rounded-xl p-4">
                <h3 className="text-sm font-semibold text-[#A1A1AA] mb-3 flex items-center gap-2">
                  <Lock className="w-4 h-4 text-[#FF2E63]" />
                  Private Photo Requests ({privateRequests.length})
                </h3>
                <div className="space-y-3">
                  {privateRequests.map((request, i) => (
                    <div 
                      key={request.id}
                      className="flex items-center gap-3 p-3 bg-white/5 rounded-lg"
                    >
                      <button
                        onClick={() => navigate(`/profile/${request.from_user_id}`)}
                        className="flex-shrink-0"
                      >
                        <img
                          src={request.from_user_photo 
                            ? (request.from_user_photo.startsWith('data:') || request.from_user_photo.startsWith('http') 
                              ? request.from_user_photo 
                              : `data:image/jpeg;base64,${request.from_user_photo}`)
                            : sampleImages[i % sampleImages.length]
                          }
                          alt={request.from_user_name}
                          className="w-12 h-12 rounded-lg object-cover"
                        />
                      </button>
                      <div className="flex-1 min-w-0">
                        <button 
                          onClick={() => navigate(`/profile/${request.from_user_id}`)}
                          className="font-semibold text-[#EDEDED] truncate block hover:text-[#FF2E63] transition-colors"
                        >
                          {request.from_user_name}
                        </button>
                        <p className="text-xs text-[#52525B]">{formatTime(request.timestamp)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          data-testid={`grant-access-${request.from_user_id}`}
                          onClick={() => handleGrantAccess(request.from_user_id, request.id)}
                          disabled={grantingAccess[request.id]}
                          size="sm"
                          className="bg-[#FF2E63] hover:bg-[#E01E50] text-white px-3"
                        >
                          <Unlock className="w-4 h-4 mr-1" />
                          Share
                        </Button>
                        <Button
                          data-testid={`deny-request-${request.from_user_id}`}
                          onClick={() => handleDenyRequest(request.id)}
                          disabled={grantingAccess[request.id]}
                          size="sm"
                          variant="ghost"
                          className="text-[#A1A1AA] hover:text-white hover:bg-white/10"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Favorites Tab */}
          <TabsContent value="favorites">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-2 border-[#FF2E63] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : favorites.length === 0 ? (
              <EmptyState 
                icon={Heart}
                title="No favorites yet"
                description="Add people to your favorites by tapping the heart on their profile"
              />
            ) : (
              <div className="glass rounded-xl p-4">
                <h3 className="text-sm font-semibold text-[#A1A1AA] mb-3">
                  Your Favorites ({favorites.length})
                </h3>
                <div className="space-y-1">
                  {favorites.map((user, i) => (
                    <UserCard key={user.id} user={user} index={i} />
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Viewers Tab */}
          <TabsContent value="viewers">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-2 border-[#FF2E63] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : viewers.length === 0 ? (
              <EmptyState 
                icon={Eye}
                title="No profile views yet"
                description="When someone views your profile, they'll appear here"
              />
            ) : (
              <div className="glass rounded-xl p-4">
                <h3 className="text-sm font-semibold text-[#A1A1AA] mb-3">
                  Who Viewed You ({viewers.length})
                </h3>
                <div className="space-y-1">
                  {viewers.map((user, i) => (
                    <UserCard 
                      key={user.id} 
                      user={user} 
                      index={i} 
                      showTime={true}
                      timeLabel={formatTime(user.viewed_at)}
                    />
                  ))}
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <BottomNav />
    </div>
  );
};

export default Activity;
